import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useReducer, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import { Tooltip } from '@grandvisionhq/common';
import { Heading } from '@grandvisionhq/elements';
import { useIntl } from '@grandvisionhq/state';
import { FormContext } from '../Form/Form.js';
import { FormControl } from '../FormControl/FormControl.js';
import Select from '../Select/Select.js';
import { DateFormat } from '../types.js';
import { DATES_RANGE } from '../Util/validations.js';

const createOptions = (min, max)=>{
    let options = {};
    for(let i = min; i <= max; i++){
        const value = i.toString();
        options = swcHelpers.objectSpread({}, options, {
            [value]: value
        });
    }
    return options;
};
const reducer = (state, action)=>{
    const { day , month , year , isTouched  } = state;
    switch(action.type){
        case 'day':
            return {
                day: action.payload,
                month,
                year,
                isTouched: isTouched || !!month && !!year
            };
        case 'month':
            return {
                day,
                month: action.payload,
                year,
                isTouched: isTouched || !!day && !!year
            };
        case 'year':
            return {
                day,
                month,
                year: action.payload,
                isTouched: isTouched || !!day && !!month
            };
        case 'reset':
            return initialize(action.payload);
        default:
            console.warn(`Unknown action: ${action}`);
            return state;
    }
};
// If we get the day back from the details call, it might be in a Date format, which splits the day into a day + time and zone, we want to trim it to just the day if this is the case (checkout)
const formatDayFromDetailsCall = (day)=>{
    return (day === null || day === void 0 ? void 0 : day.length) > 2 ? day.split('T')[0] : day;
};
const initialize = (initialState)=>{
    if (initialState) {
        const [year, month, day] = initialState.value.split('-');
        return {
            day: formatDayFromDetailsCall(day) || '',
            month: month || '',
            year: year || '',
            isTouched: initialState.isTouched || false
        };
    }
    return {
        day: '',
        month: '',
        year: '',
        isTouched: false
    };
};
const DateOfBirthSelect = ({ dateFormat =DateFormat.dmy , minYear , maxYear , dayPlaceholder , monthPlaceholder , yearPlaceholder , errorMessage , label , tooltipLabel , onChange , initialState , validations , required =false , hideAsteriskSign =false , hasHeadingLabel =false , subTitle , name ='dateOfBirth' ,  })=>{
    const [state, dispatch] = useReducer(reducer, initialState, initialize);
    const { day , month , year , isTouched  } = state;
    const readonly = initialState && initialState.readonly;
    const { getLabel  } = useIntl();
    const value = useMemo(()=>year && month && day ? [
            year,
            month,
            day
        ].join('-') : ''
    , [
        state
    ]);
    const isValid = useMemo(()=>!validations || FormControl.validate(validations, required, value)
    , [
        validations,
        value
    ]);
    useEffect(()=>{
        dispatch({
            type: 'reset',
            payload: initialState
        });
    }, [
        initialState && initialState.value,
        initialState && initialState.isTouched
    ]);
    useEffect(()=>{
        onChange && onChange(swcHelpers.objectSpread({
            name,
            value,
            isValid,
            isTouched
        }, readonly === undefined ? {} : {
            readonly
        }));
    }, [
        onChange,
        name,
        value,
        isValid,
        isTouched
    ]);
    const classes = classnames({
        _is_valid: isValid && isTouched && !readonly,
        _is_invalid: !isValid && isTouched && !readonly
    });
    const handleDayChange = React.useCallback((payload)=>{
        dispatch({
            type: 'day',
            payload: payload.padStart(2, '0')
        });
    }, []);
    const handleMonthChange = React.useCallback((payload)=>{
        dispatch({
            type: 'month',
            payload: payload.padStart(2, '0')
        });
    }, []);
    const handleYearChange = React.useCallback((payload)=>{
        dispatch({
            type: 'year',
            payload
        });
    }, []);
    // Convert dateFormat string to array
    const format = [
        ...dateFormat
    ];
    const selectionStart = React.useMemo(()=>{
        if (maxYear) {
            return String(maxYear);
        }
        if (minYear) {
            return String(minYear);
        }
        return String(new Date().getFullYear() - 18);
    }, [
        minYear,
        maxYear
    ]);
    const selectIdDay = `day-${name}`;
    const selectIdMonth = `month-${name}`;
    const selectIdYear = `year-${name}`;
    const selectInputs = {
        d: React.useMemo(()=>/*#__PURE__*/ React.createElement(Select, {
                className: classes,
                id: selectIdDay,
                name: "day",
                key: selectIdDay,
                placeholder: dayPlaceholder,
                options: createOptions(DATES_RANGE.DAY.min, DATES_RANGE.DAY.max),
                onChange: handleDayChange,
                // Remove leading 0, the BE might send the number back as 01, we want to make sure the intial state shows correctly
                initialState: {
                    value: day.replace(/^0+/, '')
                },
                disabled: readonly,
                "aria-invalid": !isValid
            })
        , [
            classes,
            name,
            dayPlaceholder,
            createOptions,
            DATES_RANGE.DAY.min,
            DATES_RANGE.DAY.max,
            day,
            readonly, 
        ]),
        m: React.useMemo(()=>/*#__PURE__*/ React.createElement(Select, {
                className: classes,
                id: selectIdMonth,
                name: "month",
                key: selectIdMonth,
                placeholder: monthPlaceholder,
                options: createOptions(DATES_RANGE.MONTH.min, DATES_RANGE.MONTH.max),
                onChange: handleMonthChange,
                // Remove leading 0, the BE might send the number back as 01, we want to make sure the intial state shows correctly
                initialState: {
                    value: month.replace(/^0+/, '')
                },
                disabled: readonly,
                "aria-invalid": !isValid
            })
        , [
            classes,
            name,
            monthPlaceholder,
            createOptions,
            DATES_RANGE.MONTH.min,
            DATES_RANGE.MONTH.max,
            month,
            readonly, 
        ]),
        y: React.useMemo(()=>/*#__PURE__*/ React.createElement(Select, {
                className: classes,
                id: selectIdYear,
                name: "year",
                key: selectIdYear,
                placeholder: yearPlaceholder,
                options: createOptions(minYear !== null && minYear !== void 0 ? minYear : DATES_RANGE.YEAR_OF_BIRTH.min, maxYear !== null && maxYear !== void 0 ? maxYear : DATES_RANGE.YEAR_OF_BIRTH.max),
                onChange: handleYearChange,
                initialState: {
                    value: year
                },
                disabled: readonly,
                selectionStart: selectionStart,
                sortOptionsBy: (a, b)=>b.localeCompare(a, 'en', {
                        sensitivity: 'base',
                        usage: 'sort'
                    })
                ,
                "aria-invalid": !isValid
            })
        , [
            classes,
            name,
            yearPlaceholder,
            createOptions,
            minYear,
            maxYear,
            DATES_RANGE.YEAR_OF_BIRTH.min,
            DATES_RANGE.YEAR_OF_BIRTH.max,
            year,
            readonly, 
        ])
    };
    const selectLabels = {
        d: /*#__PURE__*/ React.createElement("label", {
            className: "sr-only",
            htmlFor: selectIdDay
        }, label, ", ", getLabel('fields.date.day')),
        m: /*#__PURE__*/ React.createElement("label", {
            className: "sr-only",
            htmlFor: selectIdMonth
        }, label, ", ", getLabel('fields.date.month')),
        y: /*#__PURE__*/ React.createElement("label", {
            className: "sr-only",
            htmlFor: selectIdYear
        }, label, ", ", getLabel('fields.date.year'))
    };
    return /*#__PURE__*/ React.createElement("fieldset", {
        className: "form-control date-of-birth-select"
    }, label && /*#__PURE__*/ React.createElement("div", {
        className: "form-control__label"
    }, hasHeadingLabel ? /*#__PURE__*/ React.createElement(Heading, {
        id: name,
        variant: "smallest",
        component: "h2"
    }, label) : /*#__PURE__*/ React.createElement("legend", {
        className: "form-legend",
        id: name
    }, label), required && !hideAsteriskSign && /*#__PURE__*/ React.createElement("span", {
        className: "form-control__asterisk"
    }, "*"), tooltipLabel && /*#__PURE__*/ React.createElement(Tooltip, {
        id: `${name}-tooltip`,
        message: tooltipLabel
    })), subTitle && /*#__PURE__*/ React.createElement("p", {
        className: "date-of-birth-select__subtitle"
    }, subTitle), /*#__PURE__*/ React.createElement("div", {
        className: "date-of-birth-select__container"
    }, format.map((entry)=>/*#__PURE__*/ React.createElement(React.Fragment, {
            key: entry
        }, selectLabels[entry], selectInputs[entry])
    )), !isValid && isTouched && /*#__PURE__*/ React.createElement("div", {
        className: "form-control__error"
    }, errorMessage));
};
const MemoizedDateOfBirthSelect = /*#__PURE__*/ React.memo(DateOfBirthSelect);
MemoizedDateOfBirthSelect.displayName = 'DateOfBirthSelect';
const ConnectedDateOfBirthSelect = (props)=>/*#__PURE__*/ React.createElement(FormContext.Consumer, null, (context = {})=>/*#__PURE__*/ React.createElement(MemoizedDateOfBirthSelect, swcHelpers.extends({}, props, {
            onChange: context.onChange || props.onChange,
            initialState: context.getInitialState && context.getInitialState(props.name || 'dateOfBirth') || props.initialState
        }))
    )
;
ConnectedDateOfBirthSelect.defaultProps = swcHelpers.objectSpread({}, DateOfBirthSelect.defaultProps);
var ConnectedDateOfBirthSelect$1 = ConnectedDateOfBirthSelect;

export { DateOfBirthSelect, ConnectedDateOfBirthSelect$1 as default };
//# sourceMappingURL=DateOfBirthSelect.js.map
